import React from 'react';
import { AppState } from '../Store/Types';
import { connect } from 'react-redux';
import { StegVisning } from '../Store/StegVisning/Types';
import { setCollapsed } from '../Store/StegVisning/Actions';

export interface VeilederStegBaseProps {
    sporsmalId: string;
    tittel: JSX.Element | null;
    forklaring: JSX.Element | null;
    classModifier?: string;
}

export interface VeilederStegDefaultProps {
    neverCollapse?: boolean;
    customBottomRow?: JSX.Element;
}

type VeilederStegOwnProps = VeilederStegDefaultProps &
    VeilederStegBaseProps & {
        displayElement: JSX.Element | null;
        inputArea: JSX.Element;
        canCollapse: boolean;
    };

type VeilederStegProps = VeilederStegOwnProps & MapStateToProps & MapDispatchToProps;

class VeilederStegComponent<T> extends React.PureComponent<VeilederStegProps> {
    static defaultProps: VeilederStegDefaultProps = {
        neverCollapse: false,
        customBottomRow: undefined,
    };

    parentDivRef = React.createRef<HTMLDivElement>();
    headerRowRef = React.createRef<HTMLButtonElement>();
    inputParentDivRef = React.createRef<HTMLDivElement>();

    constructor(props: VeilederStegProps) {
        super(props);

        this.handleClick = this.handleClick.bind(this);
        this.handleOnKeyUp = this.handleOnKeyUp.bind(this);
        this.handleDocumentOnFocus = this.handleDocumentOnFocus.bind(this);
    }

    private handleDocumentOnFocus = (ev: FocusEvent): void => {
        const { canCollapse, stegState, stegVisningSetCollapsed, neverCollapse } = this.props;

        const targetElement = ev.target as Element;
        const parentDiv = this.parentDivRef.current;

        if (
            neverCollapse !== true &&
            canCollapse &&
            stegState.isCollapsed !== true &&
            parentDiv !== null &&
            !parentDiv.contains(targetElement) &&
            parent !== ev.target
        ) {
            stegVisningSetCollapsed({ id: stegState.id, isCollapsed: true });
        }
    };

    componentDidMount(): void {
        if (this.props.neverCollapse !== true) {
            document.addEventListener('focusin', this.handleDocumentOnFocus, false);
        }
    }

    componentWillUnmount(): void {
        if (this.props.neverCollapse !== true) {
            document.removeEventListener('focusin', this.handleDocumentOnFocus, false);
        }
    }

    handleClick = (e: React.SyntheticEvent): void => {
        const { stegState, stegVisningSetCollapsed, canCollapse, neverCollapse } = this.props;
        const canReCollapse = neverCollapse !== true && !stegState.isCollapsed && canCollapse;
        if (canReCollapse) {
            stegVisningSetCollapsed({ id: stegState.id, isCollapsed: true });
        } else if (stegState.isCollapsed) {
            stegVisningSetCollapsed({ id: stegState.id, isCollapsed: false });
        }
    };

    handleOnKeyUp(e: React.KeyboardEvent<HTMLDivElement>): void {

        if(e.key != "Enter")
            return;

        const { stegState, stegVisningSetCollapsed } = this.props;
        if (stegState.isCollapsed && e.target !== this.headerRowRef.current) {
            stegVisningSetCollapsed({ id: stegState.id, isCollapsed: false });
        } else if (!stegState.isCollapsed) {
            stegVisningSetCollapsed({ id: stegState.id, isCollapsed: true });
        }
    }

    render(): JSX.Element {
        const {
            displayElement,
            tittel,
            sporsmalId,
            forklaring,
            inputArea,
            classModifier,
            canCollapse,
            customBottomRow,
            stegState,
            neverCollapse,
        } = this.props;

        const isCollapsed = stegState.isCollapsed && canCollapse;
        const canReCollapse = neverCollapse !== true && !stegState.isCollapsed && canCollapse;

        return (
            <div
                ref={this.parentDivRef}
                className={[
                    'c-veileder__panel',
                    isCollapsed ? 'c-veileder__panel--collapsed' : '',
                    !isCollapsed && canReCollapse ? 'c-veileder__panel--uncollapsed' : '',
                    classModifier ? classModifier : 'c-veileder__panel--color',
                ].join(' ')}
                id={sporsmalId}
            >
                <div
                    className={['c-veileder__panel-row', 'c-veileder__panel-row--clickable'].join(' ')}
                    onClick={this.handleClick}
                    onKeyUp={this.handleOnKeyUp}
                    role={(!canReCollapse && !isCollapsed ? undefined : "button")}
                    tabIndex={(!canReCollapse && !isCollapsed ? undefined : 0)}
                    aria-expanded={(!canReCollapse && !isCollapsed ? undefined : !isCollapsed)}
                >
                    <div className="c-veileder__content c-veileder__content--header">
                        <h3 className="c-veileder__tittel">{tittel}</h3>
                    </div>
                    <div className="c-veileder__content c-veileder__content--header c-veileder__content--indented">
                        {!stegState.isCollapsed ? null : displayElement}
                    </div>
                </div>

                <div className="c-veileder__panel-row">
                    <div className="c-veileder__content c-veileder__content--textarea">{forklaring}</div>
                    <div
                        ref={this.inputParentDivRef}
                        className="c-veileder__content c-veileder__content--inputarea c-veileder__content--indented"
                    >
                        {inputArea}
                    </div>
                </div>
                {customBottomRow ?? null}
            </div>
        );
    }
}

interface MapStateToProps {
    stegState: StegVisning;
}

interface MapDispatchToProps {
    stegVisningSetCollapsed: typeof setCollapsed;
}

export default connect(
    (state: AppState, ownProps: VeilederStegOwnProps): MapStateToProps => {
        const stegState = state.stegVisning[ownProps.sporsmalId]
            ? state.stegVisning[ownProps.sporsmalId]
            : { id: ownProps.sporsmalId, isCollapsed: false };
        return {
            stegState,
        };
    },
    {
        stegVisningSetCollapsed: setCollapsed,
    },
)(VeilederStegComponent);
